import Image from 'next/image'
import Link from 'next/link'

import { ArrowIcon } from '@/components/icons/ArrowIcon'
import type { ResourceCardData } from '@/utilities/resourceCardData'

interface Props {
  resource: ResourceCardData
  sizes?: string
}

export function ResourceCard({
  resource,
  // `main` caps at 1440 with fixed 64px gutters, so the card stops growing at 416px - without the
  // min-width clause a 2560px viewport fetches for 845px.
  sizes = '(max-width: 640px) 300px, (max-width: 1024px) 320px, (min-width: 1440px) 416px, 33vw',
}: Props) {
  const { title, description, image, href, ctaLabel } = resource
  const imageUrl = image?.url || null

  return (
    // One link for the whole card keeps it to a single tab stop, and the image alt stays
    // empty because the title beside it would otherwise be announced twice.
    <Link
      href={href}
      className="group flex flex-col gap-3.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black"
    >
      <div
        className="relative w-full overflow-hidden rounded-[8px] bg-civo-cloud"
        style={{ aspectRatio: '416/233' }}
      >
        {imageUrl && (
          <Image
            src={imageUrl}
            alt=""
            fill
            className="object-cover motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-in-out motion-safe:group-hover:scale-105"
            sizes={sizes}
          />
        )}
      </div>

      <h3 className="subheading-medium mb-0 underline decoration-transparent transition-colors duration-300 ease-in-out group-hover:decoration-current">
        {title}
      </h3>

      {description && (
        <p className="body-copy-small mb-0 line-clamp-2 text-black/70">{description}</p>
      )}

      <span className="nav-copy inline-flex items-center gap-1">
        {ctaLabel}
        <span aria-hidden="true" className="inline-flex">
          <ArrowIcon className="size-4 shrink-0 motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-in-out motion-safe:group-hover:translate-x-1" />
        </span>
      </span>
    </Link>
  )
}
